import React from 'react';
import { BreadcrumbInputReference, BreadcrumbItem } from '@/types/breadcrumb';
import { BuilderState } from '@/types/builder';
import { IconChevronRight } from '@tabler/icons-react';

export const BREADCRUMB_COMPONENT_NAME = 'Breadcrumbs';
export const BREADCRUMB_MODEL_NAME = 'breadcrumb';

export type BreadcrumbsProps = {
  pageTitle?: string;
  // This is use at page level to get the breadcrumb data from the Builder API.
  breadcrumb?: BreadcrumbInputReference;
  builderState: BuilderState;
};

export const Breadcrumbs: React.FC<BreadcrumbsProps> = (props) => {
  const {
    pageTitle,
    builderState: {
      state: { breadcrumbs },
    },
  } = props;

  if (!breadcrumbs?.length) return null;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      ...breadcrumbs.map(({ name, path }, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name,
        item: path,
      })),
      {
        '@type': 'ListItem',
        position: breadcrumbs.length + 1,
        name: pageTitle,
      },
    ],
  };

  const mobileParent = {
    ...breadcrumbs[breadcrumbs.length - 1],
  };

  mobileParent.name = '...';

  return (
    <nav aria-label="Breadcrumb" className="border-dark-100 border-b" data-testid="breadcrumbs">
      <div className="tc-container my-5 mb-4">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

        <ul className="text-dark m-0 flex list-none items-center pl-0">
          {/* Mobile: show only parent breadcrumb with ellipsis */}
          {mobileParent && <BreadcrumbLink breadcrumb={mobileParent} className="sm:hidden" />}

          {/* Desktop: full breadcrumb trail */}
          <li className="m-0 hidden sm:flex">
            <ul className="m-0 flex items-center pl-0">
              {breadcrumbs.map((breadcrumb, idx) => (
                <BreadcrumbLink key={`${breadcrumb.name}_${idx}`} breadcrumb={breadcrumb} />
              ))}
            </ul>
          </li>

          <li className="truncate font-medium whitespace-nowrap" data-testid="breadcrumb-page">
            {pageTitle}
          </li>
        </ul>
      </div>
    </nav>
  );
};

const BreadcrumbLink: React.FC<{ breadcrumb: BreadcrumbItem; className?: string }> = ({
  breadcrumb: { name, path },
  className = '',
}) => (
  <li
    className={`hover:text-primary m-0 flex items-center whitespace-nowrap underline underline-offset-4 ${className}`}
  >
    <a href={path}>{name}</a>
    <IconChevronRight className="mx-1 h-4 w-4" aria-hidden="true" />
  </li>
);
